import React, { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { pageTitleKey } from "config/pageTitles";
import { getPageTitle, subscribePageTitle } from "./pageTitle";

const SITE = "Codenec";

/* Rendered once in App.js. Keeps document.title in step with the route and
   the language ("Log in · Codenec"), and after the first page tells screen
   readers which page the app moved to, since a client-side route change is
   silent otherwise. Focus is left where it is. Titles: config/pageTitles.js,
   or the one a page sets with usePageTitle (./pageTitle.js), which wins. */
const RouteAnnouncer = () => {
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const named = useSyncExternalStore(subscribePageTitle, getPageTitle);
  const key = pageTitleKey(pathname);
  const title = named || (key ? t(key) : "");

  // `id` gives each announcement a fresh node, so the same title twice in a
  // row is still read out.
  const [announcement, setAnnouncement] = useState({ id: 0, text: "" });
  const lastPath = useRef(null);
  const latestTitle = useRef(title);

  useEffect(() => {
    latestTitle.current = title;
    document.title = title ? `${title} · ${SITE}` : SITE;
  }, [title]);

  // A tick after the route changes, so a page that names itself has done so
  // in its own effect by the time the title is read out.
  useEffect(() => {
    const moved = lastPath.current !== null && lastPath.current !== pathname;
    lastPath.current = pathname;
    if (!moved) return undefined;
    const timer = setTimeout(() => {
      setAnnouncement((prev) => ({ id: prev.id + 1, text: latestTitle.current || SITE }));
    }, 0);
    return () => clearTimeout(timer);
  }, [pathname]);

  return (
    <div className="sr-only" aria-live="polite" aria-atomic="true">
      {announcement.id > 0 && <span key={announcement.id}>{announcement.text}</span>}
    </div>
  );
};

export default RouteAnnouncer;
